// Loading, empty, error and confirmation states. Every screen has a designed state for each.
import { type ReactNode, useEffect, useRef } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';

type AlertProps = {
  tone: 'error' | 'warn' | 'info';
  children: ReactNode;
  /** Short request ID, so a problem can be found in the logs. */
  reference?: string;
};

export function Alert({ tone, children, reference }: AlertProps) {
  return (
    <div className={cx('alert', tone)} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon name={tone === 'error' ? 'alert' : 'info'} />
      <span>
        {children}
        {reference && <span className="ref">Ref {reference}</span>}
      </span>
    </div>
  );
}

type EmptyProps = { icon: IconName; title: string; children?: ReactNode; action?: ReactNode };

/** Nothing here yet: say what will appear and how to start. */
export function EmptyState({ icon, title, children, action }: EmptyProps) {
  return (
    <div className="empty">
      <span className="glyph lg">
        <Icon name={icon} />
      </span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action && <div style={{ marginTop: 'var(--s5)' }}>{action}</div>}
    </div>
  );
}

export function Spinner({ small }: { small?: boolean }) {
  return <span className={cx('spinner', small && 'sm')} role="status" aria-label="Loading" />;
}

/** Gray placeholder bar while content loads. */
export function Skeleton({ width = '100%', height = 14 }: { width?: string; height?: number }) {
  return <div className="skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** A short confirmation at the bottom of the screen ("Saved"). */
export function Toast({ children, icon = 'check' }: { children: ReactNode; icon?: IconName }) {
  return (
    <div className="toast" role="status">
      <Icon name={icon} />
      {children}
    </div>
  );
}

type SheetProps = { title: string; children?: ReactNode; actions: ReactNode; onClose: () => void };

/** Confirmation that slides up on a phone and sits centered on a laptop. Esc closes it. */
export function Sheet({ title, children, actions, onClose }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="sheet-dialog"
      aria-label={title}
      // The cleanup above closes the dialog; in development React mounts twice, and that close event can arrive
      // after the dialog has reopened. Only a dialog that is really shut (Esc) counts as closed.
      onClose={(e) => {
        if (!e.currentTarget.open) onClose();
      }}
    >
      <div className="sheet stack">
        <h3>{title}</h3>
        {children}
        <div className="actions">{actions}</div>
      </div>
    </dialog>
  );
}
